/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { DnsRecord, DnsRecord$inboundSchema } from "./dnsrecord.js";
import { DomainConnect, DomainConnect$inboundSchema } from "./domainconnect.js";

/**
 * The verification status of the domain.
 *
 * @remarks
 *
 * - `pending`: the domain is created. No DNS check has completed yet.
 * - `verifying`: Teral checks the DNS records approximately each minute.
 * - `verified`: the domain is verified. Teral has configured routing and requested a certificate.
 * - `failed`: the required DNS records did not appear within 24 hours. Fix the records, then retry verification.
 */
export const DomainStatus = {
  Pending: "pending",
  Verifying: "verifying",
  Verified: "verified",
  Failed: "failed",
} as const;
/**
 * The verification status of the domain.
 *
 * @remarks
 *
 * - `pending`: the domain is created. No DNS check has completed yet.
 * - `verifying`: Teral checks the DNS records approximately each minute.
 * - `verified`: the domain is verified. Teral has configured routing and requested a certificate.
 * - `failed`: the required DNS records did not appear within 24 hours. Fix the records, then retry verification.
 */
export type DomainStatus = ClosedEnum<typeof DomainStatus>;

export type Domain = {
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  id: string;
  /**
   * Fully qualified domain name attached to the environment.
   */
  domain: string;
  /**
   * The project the domain's environment belongs to.
   */
  projectId: string;
  /**
   * The app the domain's environment belongs to.
   */
  appId: string;
  /**
   * The environment this domain serves. Traffic to the domain reaches whatever is currently
   *
   * @remarks
   * deployed to this environment.
   */
  environmentId: string;
  /**
   * The verification status of the domain.
   *
   * @remarks
   *
   * - `pending`: the domain is created. No DNS check has completed yet.
   * - `verifying`: Teral checks the DNS records approximately each minute.
   * - `verified`: the domain is verified. Teral has configured routing and requested a certificate.
   * - `failed`: the required DNS records did not appear within 24 hours. Fix the records, then retry verification.
   */
  status: DomainStatus;
  /**
   * Why the most recent verification attempt did not succeed, in plain language.
   *
   * @remarks
   * Omitted while verification is progressing normally.
   */
  verificationError?: string | undefined;
  /**
   * The DNS records this domain needs. Create each record at your DNS provider.
   *
   * @remarks
   * Each record has a `verified` flag. The flag shows whether Teral has read that record back,
   * so it tells you which records are still missing.
   */
  dnsRecords: Array<DnsRecord>;
  /**
   * One-click setup at the domain's DNS provider. Omitted entirely when the provider does not support
   *
   * @remarks
   * Domain Connect or discovery failed, so the object's presence is the signal that the shortcut is
   * available and both of its fields are filled.
   */
  domainConnect?: DomainConnect | undefined;
  /**
   * Unix timestamp in milliseconds when the domain was created. The 24 hour verification window runs from here.
   */
  createdAt: number;
  /**
   * Unix timestamp in milliseconds of the last change to this domain. Omitted if it has never changed.
   */
  updatedAt?: number | undefined;
};

/** @internal */
export const DomainStatus$inboundSchema: z.ZodNativeEnum<typeof DomainStatus> =
  z.nativeEnum(DomainStatus);

/** @internal */
export const Domain$inboundSchema: z.ZodType<Domain, z.ZodTypeDef, unknown> = z
  .object({
    id: z.string(),
    domain: z.string(),
    projectId: z.string(),
    appId: z.string(),
    environmentId: z.string(),
    status: DomainStatus$inboundSchema,
    verificationError: z.string().optional(),
    dnsRecords: z.array(DnsRecord$inboundSchema),
    domainConnect: DomainConnect$inboundSchema.optional(),
    createdAt: z.number().int(),
    updatedAt: z.number().int().optional(),
  });

export function domainFromJSON(
  jsonString: string,
): SafeParseResult<Domain, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Domain$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Domain' from JSON`,
  );
}
